<template>
    <div class="case-detail" v-if="detail">
        <section class="page-header">
            <div class="container">
                <div class="breadcrumb">
                    <el-breadcrumb separator="/">
                        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                        <el-breadcrumb-item :to="{ path: SUCCESS_CASES_LIST_PATH }">成功案例</el-breadcrumb-item>
                        <el-breadcrumb-item>{{ detail.title }}</el-breadcrumb-item>
                    </el-breadcrumb>
                </div>
            </div>
        </section>

        <section class="article">
            <div class="container">
                <div class="article-container">
                    <div class="article-header">
                        <h1 class="article-title">{{ detail.title }}</h1>
                        <div class="article-meta">
                            <div class="meta-item">
                                <el-icon>
                                    <Calendar />
                                </el-icon>
                                <span>{{ formatDate(detail.date) }}</span>
                            </div>
                            <div class="meta-item">
                                <el-icon>
                                    <View />
                                </el-icon>
                                <span>{{ detail.views }} 次浏览</span>
                            </div>
                            <div class="meta-item">
                                <el-tag :type="getCategoryType(detail.category)">
                                    {{ getSuccessCaseCategoryLabel(detail.category) }}
                                </el-tag>
                            </div>
                        </div>
                    </div>

                    <div class="article-cover" data-aos="fade-up">
                        <el-image :src="detail.image" fit="cover" />
                    </div>

                    <div class="article-content" data-aos="fade-up">
                        <div class="content" v-html="detail.content"></div>
                    </div>

                    <div class="article-tags">
                        <el-icon>
                            <Collection />
                        </el-icon>
                        <el-tag v-for="tag in detail.tags" :key="tag" class="tag" effect="plain">{{ tag }}</el-tag>
                    </div>

                    <div class="article-share">
                        <span class="share-label">分享到：</span>
                        <div class="share-buttons">
                            <el-button circle @click="handleShare('weixin')">
                                <el-icon>
                                    <ChatDotRound />
                                </el-icon>
                            </el-button>
                            <el-button circle @click="handleShare('weibo')">
                                <el-icon>
                                    <Share />
                                </el-icon>
                            </el-button>
                        </div>
                    </div>
                </div>

                <div class="related-section" v-if="relatedCards.length">
                    <h2 class="section-title">更多案例</h2>
                    <div class="articles-grid">
                        <div class="article-card" v-for="item in relatedCards" :key="item.id"
                            @click="goToDetail(item.id)" data-aos="fade-up">
                            <el-image :src="item.image" fit="cover" class="card-image" />
                            <div class="card-body">
                                <h3 class="card-title">{{ item.title }}</h3>
                                <p class="card-description">{{ item.description }}</p>
                                <div class="card-meta">
                                    <span class="date">{{ formatDate(item.date) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Calendar, View, Collection, ChatDotRound, Share } from '@element-plus/icons-vue'
import type { TagProps } from 'element-plus'
import AOS from 'aos'
import 'aos/dist/aos.css'
import {
    successCases,
    SUCCESS_CASES_LIST_PATH,
    getSuccessCaseById,
    getSuccessCaseCategoryLabel,
} from '@/data/successCases'
import type { SuccessCase, SuccessCaseCategory } from '@/data/successCases'

const route = useRoute()
const router = useRouter()

const detail = ref<SuccessCase | null>(null)

const relatedCards = computed(() => {
    const id = detail.value?.id
    if (!id) return []
    const idx = successCases.findIndex((c) => c.id === id)
    const prev = idx > 0 ? successCases[idx - 1] : null
    const next = idx < successCases.length - 1 ? successCases[idx + 1] : null
    return [prev, next].filter(Boolean) as SuccessCase[]
})

const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const getCategoryType = (value: SuccessCaseCategory): TagProps['type'] => {
    const map: Record<SuccessCaseCategory, TagProps['type']> = {
        logistics: 'primary',
        ecommerce: 'danger',
        manufacturing: 'info',
        government: 'success',
        retail: 'warning',
        hospitality: 'info',
        erp: 'warning',
        sports: 'success',
        healthcare: 'danger',
    }
    return map[value]
}

const loadDetail = () => {
    const rawId = route.params.id
    const id = Number(Array.isArray(rawId) ? rawId[0] : rawId)
    if (!Number.isFinite(id)) {
        router.replace(SUCCESS_CASES_LIST_PATH)
        return
    }
    const hit = getSuccessCaseById(id)
    if (!hit) {
        router.replace(SUCCESS_CASES_LIST_PATH)
        return
    }
    detail.value = hit
}

const handleShare = (platform: string) => {
    ElMessage.success(`分享到${platform === 'weixin' ? '微信' : '微博'}`)
}

const goToDetail = (id: number) => {
    router.push(`${SUCCESS_CASES_LIST_PATH}/${id}`)
}

onMounted(() => {
    AOS.init({ duration: 1000, once: true })
})

watch(() => route.params.id, () => loadDetail(), { immediate: true })
</script>

<style scoped lang="scss">
.case-detail {
    padding-top: 80px;

    .page-header {
        background: linear-gradient(135deg, #1890ff 0%, #604FDF 100%);
        padding: 60px 0;
        color: #fff;
        margin-bottom: 60px;

        .breadcrumb {
            :deep(.el-breadcrumb) {
                color: rgba(255, 255, 255, 0.8);

                .el-breadcrumb__item {
                    .el-breadcrumb__inner {
                        color: rgba(255, 255, 255, 0.8);

                        &:hover {
                            color: #fff;
                        }
                    }

                    &:last-child .el-breadcrumb__inner {
                        color: #fff;
                    }
                }
            }
        }
    }

    .article {
        padding: 0 0 80px;

        .article-container {
            max-width: 800px;
            margin: 0 auto;
            background: #fff;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            padding: 40px;
            margin-bottom: 60px;

            .article-header {
                margin-bottom: 30px;

                .article-title {
                    font-size: 32px;
                    color: #333;
                    margin-bottom: 20px;
                    line-height: 1.4;
                }

                .article-meta {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 20px;
                    color: #999;

                    .meta-item {
                        display: flex;
                        align-items: center;

                        .el-icon {
                            margin-right: 6px;
                        }
                    }
                }
            }

            .article-cover {
                margin: 0 -40px 30px;

                .el-image {
                    width: 100%;
                    height: 400px;
                }
            }

            .article-content {
                margin-bottom: 40px;

                :deep(.content) {
                    font-size: 16px;
                    line-height: 1.8;
                    color: #333;

                    h2 {
                        font-size: 24px;
                        margin: 30px 0 20px;
                        color: #333;
                    }

                    code {
                        background: #f5f5f5;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-size: 0.9em;
                    }

                    p {
                        margin-bottom: 20px;
                    }

                    ul {
                        margin-bottom: 20px;
                        padding-left: 20px;

                        li {
                            margin-bottom: 10px;
                        }
                    }

                    strong {
                        color: #222;
                    }
                }
            }

            .article-tags {
                display: flex;
                align-items: center;
                margin-bottom: 30px;
                flex-wrap: wrap;
                gap: 8px;

                .el-icon {
                    margin-right: 10px;
                    color: #999;
                }
            }

            .article-share {
                display: flex;
                align-items: center;
                padding-top: 30px;
                border-top: 1px solid #eee;

                .share-label {
                    margin-right: 20px;
                    color: #999;
                }

                .share-buttons {
                    display: flex;
                    gap: 10px;
                }
            }
        }

        .related-section {
            max-width: 800px;
            margin: 0 auto;

            .section-title {
                font-size: 24px;
                color: #333;
                margin-bottom: 30px;
            }

            .articles-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 30px;

                .article-card {
                    background: #fff;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    cursor: pointer;
                    transition: transform 0.3s;

                    &:hover {
                        transform: translateY(-10px);
                    }

                    .card-image {
                        width: 100%;
                        height: 200px;
                    }

                    .card-body {
                        padding: 20px;

                        .card-title {
                            font-size: 18px;
                            color: #333;
                            margin-bottom: 10px;
                            line-height: 1.4;
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            -webkit-box-orient: vertical;
                            overflow: hidden;
                        }

                        .card-description {
                            font-size: 14px;
                            color: #666;
                            margin-bottom: 15px;
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            -webkit-box-orient: vertical;
                            overflow: hidden;
                        }

                        .card-meta {
                            color: #999;
                            font-size: 14px;
                        }
                    }
                }
            }
        }
    }
}

@media (max-width: 992px) {
    .case-detail .article {
        .article-container {
            margin: 0 20px 40px;
            padding: 30px;

            .article-cover {
                margin: 0 -30px 30px;

                .el-image {
                    height: 300px;
                }
            }
        }

        .related-section {
            margin: 0 20px;
        }
    }
}

@media (max-width: 768px) {
    .case-detail {
        .page-header {
            padding: 40px 0;
        }

        .article .article-container {
            padding: 20px;

            .article-header .article-title {
                font-size: 24px;
            }

            .article-cover {
                margin: 0 -20px 20px;

                .el-image {
                    height: 200px;
                }
            }
        }

        .article .related-section .articles-grid {
            grid-template-columns: 1fr;
        }
    }
}
</style>
